import { useEffect, useState } from 'react';

import { toast } from 'sonner';

import { LEAVE_TYPES, LEAVE_TYPE_LABELS_KO, todayIso } from '@lemoment/shared';
import type { CreateLeaveRequest, Leave, LeaveType } from '@lemoment/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lemoment/ui-kit';

import { useCreateLeave, useUpdateLeave } from '../hooks';
import { DateField } from './DateField';

interface LeaveFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mode when provided. */
  leave?: Leave;
  /** Prefill for create mode (e.g. from a parsed command). */
  initial?: Partial<CreateLeaveRequest>;
}

export const LeaveFormDialog = ({
  open,
  onOpenChange,
  leave,
  initial,
}: LeaveFormDialogProps) => {
  const isEdit = Boolean(leave);
  const [type, setType] = useState<LeaveType>('annual');
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [reason, setReason] = useState('');

  const createLeave = useCreateLeave();
  const updateLeave = useUpdateLeave();
  const isPending = createLeave.isPending || updateLeave.isPending;
  const isAnnual = type === 'annual';

  // Sync form state whenever the dialog opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    const source = leave ?? initial;
    setType(source?.type ?? 'annual');
    setStartDate(source?.startDate ?? todayIso());
    setEndDate(
      leave?.endDate ?? initial?.endDate ?? source?.startDate ?? todayIso(),
    );
    setReason(source?.reason ?? '');
  }, [open, leave, initial]);

  // Keep the end date from drifting before the start date.
  const handleStartChange = (next: string) => {
    setStartDate(next);
    if (endDate < next) {
      setEndDate(next);
    }
  };

  const handleSubmit = () => {
    if (!startDate) {
      toast.error('시작일을 입력하세요.');
      return;
    }
    if (isAnnual && endDate < startDate) {
      toast.error('종료일은 시작일 이후여야 합니다.');
      return;
    }

    const body: CreateLeaveRequest = {
      type,
      startDate,
      endDate: isAnnual ? endDate : undefined,
      reason: reason.trim() || undefined,
    };

    if (isEdit && leave) {
      updateLeave.mutate(
        { id: leave.id, body },
        {
          onSuccess: () => {
            toast.success('근태를 수정했습니다.');
            onOpenChange(false);
          },
          onError: () => toast.error('수정에 실패했습니다.'),
        },
      );
      return;
    }

    createLeave.mutate(body, {
      onSuccess: () => {
        toast.success('근태를 등록했습니다.');
        onOpenChange(false);
      },
      onError: () => toast.error('등록에 실패했습니다.'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-semibold tracking-tight">
            {isEdit ? '근태 수정' : '근태 등록'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label className="eyebrow text-muted-foreground">유형</Label>
            <Select value={type} onValueChange={(v) => setType(v as LeaveType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {LEAVE_TYPE_LABELS_KO[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="eyebrow text-muted-foreground">시작일</Label>
            <DateField value={startDate} onChange={handleStartChange} />
          </div>

          {isAnnual && (
            <div className="flex flex-col gap-1.5">
              <Label className="eyebrow text-muted-foreground">종료일</Label>
              <DateField
                value={endDate}
                min={startDate}
                onChange={setEndDate}
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label className="eyebrow text-muted-foreground">사유</Label>
            <Input
              value={reason}
              placeholder="선택 입력"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending}
            className="bg-lemon text-lemon-foreground hover:bg-lemon/90"
          >
            {isEdit ? '저장' : '등록'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
